import type { GridCellParams } from '@mui/x-data-grid-pro';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { useCellStyles } from 'components/editor/GridDndEditor/Block/Table/useCellStyles';
import { getBodyRowIndexById, getRowClassName } from 'muiTheme/dataGridUtils';
import { useSectionsForPreview } from 'pages/editor/useSectionsForPreview';
import { useCallback, useMemo, useState } from 'react';
import type {
  BlocksFromSectionsByDocumentIdApiResponse,
  CurrencySettings,
  DocumentSectionsResponse,
  SectionByDocumentIdApiResponse,
} from 'services/documents/DocumentRepository';
import { TableTypeIdentifier } from '../../../grid/reduxStore/table.types';
import type { ColumnTypeIdentifier, TableColumnType, TableRowType } from '../../../grid/reduxStore/table.types';
import { gridPixelSize } from '../../../shared/gridConfig';
import { DEFAULT_DECIMAL_PLACES } from '../Table/defaultData';
import { getEngineRowsWithCalculatedSubtotals } from '../Table/tableUtils/getEngineRowsWithCalculatedSubtotals';
import { getIsCellEditingAllowed } from '../Table/tableUtils/customCellHelpers';
import { useTableCellModes } from '../Table/useTableCellModes';
import { PreviewBlock } from './PreviewBlock';
import PreviewTableFooter from './PreviewTableFooter';
import { isNumericColumn } from '../Table/helper.ts';
import { useApplyCustomRulesToColumnsInPreview } from './useApplyCustomRulesToColumnsInPreview';

interface PreviewTableBlockProps {
  block: BlockContent;
  isDocumentLocked: boolean;
  section: SectionByDocumentIdApiResponse;
  currencySettings?: CurrencySettings;
  rotation?: number;
}

export function updateSectionWithBlockUpdate(
  section: SectionByDocumentIdApiResponse,
  blockId: string,
  updatedRows: TableRowType[],
  updateSections: (newSections: DocumentSectionsResponse) => void,
  sections: DocumentSectionsResponse,
  sectionId: string
) {
  const updatedSection: SectionByDocumentIdApiResponse = {
    ...section,
    blocks: section.blocks.map((existingBlock) => {
      let block: BlocksFromSectionsByDocumentIdApiResponse;
      if (existingBlock.gridId === blockId && existingBlock.content) {
        block = {
          ...existingBlock,
          content: {
            ...existingBlock.content,
            rows: updatedRows,
          },
        };
      } else {
        block = existingBlock;
      }

      return block;
    }),
  };
  updateSections({ sections: sections.sections.map((sec) => (sec.id === sectionId ? updatedSection : sec)) });
}

export function PreviewTableBlock({ block, isDocumentLocked, section, currencySettings, rotation }: PreviewTableBlockProps) {
  const apiRef = useGridApiRef();
  const { sections, updateSections } = useSectionsForPreview();
  const blockId = block.blockConfig.id;
  const sectionId = section.id;
  const columnsData = block?.contentTable?.columns;

  const tableColumns = useMemo(() => (columnsData || []) as TableColumnType[], [columnsData]);
  const contentTableRows = block?.contentTable?.rows || [];
  const filteredRows = useMemo(() => contentTableRows.filter((row) => !row.isRowHidden), [contentTableRows]);
  const [allTableRows, setAllTableRows] = useState<TableRowType[]>(filteredRows);
  const { handleCellClick, handleCellModesModelChange, cellModesModel } = useTableCellModes();

  const { applyCustomRulesToColumnsInPreview } = useApplyCustomRulesToColumnsInPreview({
    blockId: block.blockConfig.id,
    pricingDecimalPlaces: block.contentTable?.metadata.pricingDecimalPlaces ?? DEFAULT_DECIMAL_PLACES,
    currencySettings,
  });

  const handleIsCellEditable = (params: GridCellParams): boolean => {
    const { row, colDef } = params;
    if (isDocumentLocked) {
      return false;
    }

    const isCellEditingAllowed = getIsCellEditingAllowed({ row, colDef });

    return isCellEditingAllowed;
  };

  const handleIsCellNumeric = (params: GridCellParams): boolean => {
    const { colDef } = params;
    if (isDocumentLocked) {
      return false;
    }

    return isNumericColumn(colDef['columnType'] as ColumnTypeIdentifier);
  };

  const updateRowsOptionalStatus = useCallback(
    (updatedRow: TableRowType) => {
      const { rows } = apiRef.current?.store?.value ?? {};
      if (!rows) {
        return;
      }

      const { dataRowIdToModelLookup, dataRowIds } = rows;
      const updatedRows = dataRowIds.map((rowId: string) =>
        dataRowIdToModelLookup[rowId].id === updatedRow.id
          ? { ...dataRowIdToModelLookup[rowId], isRowOptional: updatedRow.isRowOptional, ...updatedRow }
          : dataRowIdToModelLookup[rowId]
      );
      setAllTableRows(updatedRows);

      if (section) {
        updateSectionWithBlockUpdate(section, blockId, updatedRows, updateSections, sections, sectionId);
      }
    },
    [apiRef, section, block, blockId, sections, sectionId, updateSections]
  );

  const gridColumns = useMemo(
    () => applyCustomRulesToColumnsInPreview(tableColumns, updateRowsOptionalStatus, isDocumentLocked),
    [tableColumns, applyCustomRulesToColumnsInPreview, updateRowsOptionalStatus]
  );
  const pricingDecimalPlaces = block?.contentTable?.metadata?.pricingDecimalPlaces ?? DEFAULT_DECIMAL_PLACES;

  const enginePricing = useMemo(
    () => getEngineRowsWithCalculatedSubtotals(allTableRows, tableColumns, currencySettings, pricingDecimalPlaces),
    [allTableRows, tableColumns, currencySettings, pricingDecimalPlaces]
  );

  const { textRowsWithCalculatedSubtotalsColumn, sumOfSubtotalColumn } = enginePricing;
  const presentFrequencies = enginePricing.presentFrequencies;
  const frequencyTotals = enginePricing.frequencyTotals;
  const { tableType = null } = block?.contentTable?.metadata ?? {};
  const totalRows = block?.contentTable?.totalRows as TableRowType[];
  const isPricingTable = tableType === TableTypeIdentifier.PRICING_TABLE;
  const { cellStylesClassName, cellStyles } = useCellStyles(textRowsWithCalculatedSubtotalsColumn);
  const bodyRowIndexById = useMemo(
    () => getBodyRowIndexById(textRowsWithCalculatedSubtotalsColumn),
    [textRowsWithCalculatedSubtotalsColumn]
  );

  return (
    <PreviewBlock key={block.blockConfig.id} gridBlock={block} gridSystemInPx={gridPixelSize} rotation={rotation}>
      {cellStyles}
      <DataGridPro
        apiRef={apiRef}
        rows={textRowsWithCalculatedSubtotalsColumn}
        columns={gridColumns}
        className={`py-selected-state py-preview-table-style ${cellStylesClassName}`}
        isCellEditable={(params) => handleIsCellEditable(params)}
        onCellClick={handleCellClick}
        getCellClassName={(params) => (handleIsCellNumeric(params) ? 'py-is-numeric-cell' : '')}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        getRowClassName={(params) =>
          getRowClassName(params.row.rowType, undefined, undefined, undefined, bodyRowIndexById.get(params.row.id))
        }
        sx={{
          width: block.blockConfig.width,
          ...(isPricingTable && {
            borderRadius: 'var(--unstable_DataGrid-radius) var(--unstable_DataGrid-radius) 0 0',
          }),
        }}
      />
      {isPricingTable && (
        <PreviewTableFooter
          currencySettings={currencySettings}
          updatedTotalRows={totalRows}
          sumOfSubtotalColumn={sumOfSubtotalColumn}
          presentFrequencies={presentFrequencies}
          frequencyTotals={frequencyTotals}
          pricingDecimalPlaces={block?.contentTable?.metadata?.pricingDecimalPlaces ?? DEFAULT_DECIMAL_PLACES}
        />
      )}
    </PreviewBlock>
  );
}
